import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Button, Table } from 'react-bootstrap';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import LoadingSpinner from 'components/shared/LoadingSpinner';
import {
  BG_1,
  BG_2,
  BORDER_2,
  CHART_BAR_FILL,
  CHART_CURSOR,
  CHART_PRICE_HIGH,
  CHART_PRICE_LOW,
  CHART_PRICE_MED,
  ERROR_TEXT,
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_TITLE,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_COLOR,
  RADIUS_LG,
  SHADOW_MD,
  SHADOW_XS,
  SUCCESS_TEXT,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';
import { DASH, fmtInt, fmtMoney, fmtPrice, isNil } from 'components/upb_viewer_beta/pdp/format';
import useGet from 'lib/hooks/useGet';
import { GLOBAL_PRODUCT_SALES_PRICING } from 'lib/networking/endpoints';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = m => (m ? MONTHS[Number(m.split('-')[1]) - 1] || m : '');
// Compact $ for chart axes: $16k, $850.
const fmtMoneyK = v => {
  if (isNil(v)) return '';
  if (Math.abs(v) < 1000) return `$${Math.round(v)}`;
  const k = v / 1000;
  return `$${Number.isInteger(k) ? k : k.toFixed(1)}k`;
};

// Chart palette (design tokens — matches the design mock).
const BAR_FILL = CHART_BAR_FILL; // lighter purple "Units sold" bars
const PRICE_HIGH = CHART_PRICE_HIGH; // highest price paid (green)
const PRICE_LOW = CHART_PRICE_LOW; // lowest price paid (red)
const PRICE_MED = CHART_PRICE_MED; // median price (orange)
const CURSOR_GREY = CHART_CURSOR; // dashed vertical hover guide

// Hover card matching the mock's `.chart-tt`: bold month header + one row per
// series (colored dot · name · right-aligned value). `order` lists the dataKeys
// in display order; `valueFormatter(dataKey, value)` formats each value.
function ChartTooltip({ active, payload, label, order, valueFormatter }) {
  if (!active || !payload || !payload.length) {
    return null;
  }
  const rank = key => {
    const i = order.indexOf(key);
    return i === -1 ? order.length : i;
  };
  // De-dupe series that share a dataKey (the GMV line + its gradient-fill Area
  // both report `gmv`): keep the named series (Line) over the unnamed fill,
  // whose name falls back to the dataKey.
  const byKey = new Map();
  payload
    .filter(p => p && p.dataKey != null && p.value != null && p.dataKey !== 'band')
    .forEach(p => {
      const prev = byKey.get(p.dataKey);
      if (!prev || (prev.name === prev.dataKey && p.name !== p.dataKey)) {
        byKey.set(p.dataKey, p);
      }
    });
  const rows = [...byKey.values()].sort((a, b) => rank(a.dataKey) - rank(b.dataKey));
  if (!rows.length) {
    return null;
  }
  return (
    <div className={css(styles.tooltip)}>
      <div className={css(styles.ttMonth)}>{label}</div>
      {rows.map(r => (
        <div key={r.dataKey} className={css(styles.ttRow)}>
          <span className={css(styles.ttLbl)}>
            <span className={css(styles.ttDot)} style={{ backgroundColor: r.color }} />
            {r.name}
          </span>
          <span className={css(styles.ttVal)}>{valueFormatter(r.dataKey, r.value)}</span>
        </div>
      ))}
    </div>
  );
}
ChartTooltip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string,
  payload: PropTypes.arrayOf(PropTypes.object),
  order: PropTypes.arrayOf(PropTypes.string).isRequired,
  valueFormatter: PropTypes.func.isRequired,
};
ChartTooltip.defaultProps = { active: false, label: '', payload: [] };

// Top-left HTML legend matching the mock's `.chart-legend` (sits above the
// shaded plot panel, on the white card). Each item is a small swatch + label:
// a rounded square for bar series, a thick short line for line series.
function ChartLegend({ items }) {
  return (
    <div className={css(styles.legend)}>
      {items.map(it => (
        <span key={it.label} className={css(styles.legendItem)}>
          <span
            className={css(
              styles.legendSwatch,
              it.type === 'bar' ? styles.swatchBar : styles.swatchLine,
            )}
            style={{ backgroundColor: it.color }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}
ChartLegend.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['bar', 'line']),
    }),
  ).isRequired,
};

function PlaceholderSection({ title }) {
  return (
    <div className={css(styles.section)}>
      <h3 className={css(styles.sectionTitle)}>{title}</h3>
      <div className={css(styles.placeholder)}>No data source yet</div>
    </div>
  );
}
PlaceholderSection.propTypes = { title: PropTypes.string.isRequired };

function BetaSalesPricingTab({ productId }) {
  const [chart, setChart] = useState('gmv');
  const [distributor, setDistributor] = useState('');

  // Page-level data (cards, per-DP, cashback, distributor list) — unfiltered.
  const { data: base = {}, loading } = useGet(
    `${GLOBAL_PRODUCT_SALES_PRICING}/${productId}`,
    {},
    !productId,
  );
  // Distributor-filtered trends — only fetched when a distributor is chosen.
  const { data: filtered = {} } = useGet(
    `${GLOBAL_PRODUCT_SALES_PRICING}/${productId}`,
    { distributor },
    !productId || !distributor,
  );

  const trends = useMemo(
    () => (distributor ? filtered.trends : base.trends) || [],
    [distributor, filtered.trends, base.trends],
  );
  // Memoized so the chart rows (and the derived axis ticks below) are only
  // recomputed when the trends payload actually changes, not on every render.
  const chartData = useMemo(
    () =>
      trends.map(t => ({
        month: monthLabel(t.MONTH),
        gmv: t.GMV,
        units: t.UNITS,
        avg: t.AVG_PRICE,
        median: t.MEDIAN_PRICE,
        min: t.MIN_PRICE,
        max: t.MAX_PRICE,
        // [low, high] range for the shaded band drawn behind the price lines.
        band: [t.MIN_PRICE, t.MAX_PRICE],
      })),
    [trends],
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  const distributors = base.distributors || [];
  const perf = base.distributor_performance || [];
  const cashback = base.cashback || {};
  // Evenly-spaced "nice" axis ticks so the horizontal gridlines sit at EQUAL
  // distances (e.g. $0 / $4k / $8k / $12k / $16k) and the GMV + Units axes share
  // the same gridline levels on the dual-axis chart (4 equal intervals = 5 lines).
  const niceStep = x => {
    if (x <= 0) return 1;
    const mag = 10 ** Math.floor(Math.log10(x));
    const n = x / mag;
    const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
    return nice * mag;
  };
  const DIVISIONS = 4;
  const equalAxisFromZero = vals => {
    const max = Math.max(0, ...vals.filter(v => v != null));
    const step = niceStep((max * 1.1 || 1) / DIVISIONS);
    return Array.from({ length: DIVISIONS + 1 }, (_, i) => step * i);
  };
  const gmvTicks = equalAxisFromZero(chartData.map(d => d.gmv));
  const unitsTicks = equalAxisFromZero(chartData.map(d => d.units));
  const gmvDomainMax = gmvTicks[gmvTicks.length - 1];
  const unitsDomainMax = unitsTicks[unitsTicks.length - 1];
  // Price axis: equal steps spanning a little beyond the high/low band (not from 0).
  const priceVals = chartData.flatMap(d => [d.min, d.max]).filter(v => v != null);
  const priceLo = priceVals.length ? Math.min(...priceVals) : 0;
  const priceHi = priceVals.length ? Math.max(...priceVals) : 1;
  const priceStep = niceStep(((priceHi - priceLo) * 1.4 || priceHi || 1) / DIVISIONS);
  const priceFloor = Math.floor((priceLo - priceStep * 0.25) / priceStep) * priceStep;
  const priceCeil = Math.ceil((priceHi + priceStep * 0.25) / priceStep) * priceStep;
  const priceDomain = [priceFloor, priceCeil];
  const priceTicks = Array.from(
    { length: Math.round((priceCeil - priceFloor) / priceStep) + 1 },
    (_, i) => Number((priceFloor + priceStep * i).toFixed(2)),
  );
  const dataFrom = trends.length
    ? `${monthLabel(trends[0].MONTH)} ${(trends[0].MONTH || '').split('-')[0]}`
    : null;

  return (
    <div className={css(styles.wrap)}>
      {/* ── Charts ───────────────────────────────────────────────────── */}
      <div className={css(styles.section)}>
        <div className={css(styles.chartHead)}>
          <div className={css(styles.chartTabs)}>
            <Button
              variant="link"
              className={css(styles.chartTab, chart === 'gmv' && styles.chartTabActive)}
              onClick={() => setChart('gmv')}
            >
              GMV &amp; Units
            </Button>
            <Button
              variant="link"
              className={css(styles.chartTab, chart === 'price' && styles.chartTabActive)}
              onClick={() => setChart('price')}
            >
              Avg Price
            </Button>
          </div>
          <div className={css(styles.dpFilter)}>
            <span className={css(styles.dpLabel)}>Distributor</span>
            <select
              className={css(styles.select)}
              value={distributor}
              onChange={e => setDistributor(e.target.value)}
            >
              <option value="">All Distributors</option>
              {distributors.map(d => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>

        {dataFrom && (
          <div className={css(styles.note)}>Showing available order history (from {dataFrom}).</div>
        )}

        {chartData.length === 0 ? (
          <div className={css(styles.empty)}>No order activity in the last 12 months.</div>
        ) : chart === 'gmv' ? (
          <>
            <ChartLegend
              items={[
                { label: 'Units sold', color: BAR_FILL, type: 'bar' },
                { label: 'Monthly GMV', color: PRIMARY_COLOR, type: 'line' },
              ]}
            />
            <div className={css(styles.chartWrap)}>
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={chartData} margin={{ top: 24, right: 16, bottom: 8, left: 8 }}>
                  <defs>
                    <linearGradient id="gmvGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={PRIMARY_COLOR} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={PRIMARY_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={BORDER_2} vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={{ stroke: BORDER_2 }}
                    tick={{ fontSize: 14, fontWeight: 600, fill: FG_3 }}
                  />
                  {/* GMV on the left axis, Units on the right (matches the design). */}
                  <YAxis
                    yAxisId="gmv"
                    domain={[0, gmvDomainMax]}
                    ticks={gmvTicks}
                    interval={0}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 14, fontWeight: 600, fill: FG_3 }}
                    tickFormatter={fmtMoneyK}
                    label={{
                      value: 'GMV',
                      position: 'top',
                      offset: 12,
                      fill: FG_2,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  />
                  <YAxis
                    yAxisId="units"
                    orientation="right"
                    domain={[0, unitsDomainMax]}
                    ticks={unitsTicks}
                    interval={0}
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 14, fontWeight: 600, fill: FG_3 }}
                    tickFormatter={fmtInt}
                    label={{
                      value: 'UNITS',
                      position: 'top',
                      offset: 12,
                      fill: FG_2,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  />
                  <Tooltip
                    cursor={false}
                    content={
                      <ChartTooltip
                        order={['units', 'gmv']}
                        valueFormatter={(key, val) => (key === 'gmv' ? fmtMoney(val) : fmtInt(val))}
                      />
                    }
                  />
                  <Bar
                    yAxisId="units"
                    dataKey="units"
                    name="Units sold"
                    fill={BAR_FILL}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={46}
                  />
                  {/* Soft gradient fill under the GMV line. */}
                  <Area
                    yAxisId="gmv"
                    type="linear"
                    dataKey="gmv"
                    stroke="none"
                    fill="url(#gmvGradient)"
                    legendType="none"
                    tooltipType="none"
                    activeDot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    yAxisId="gmv"
                    type="linear"
                    dataKey="gmv"
                    name="Monthly GMV"
                    stroke={PRIMARY_COLOR}
                    strokeWidth={3.5}
                    dot={{ r: 5, fill: WHITE, stroke: PRIMARY_COLOR, strokeWidth: 3.5 }}
                    activeDot={{ r: 7, fill: WHITE, stroke: PRIMARY_COLOR, strokeWidth: 3.5 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <>
            <ChartLegend
              items={[
                { label: 'Highest price paid', color: PRICE_HIGH, type: 'line' },
                { label: 'Avg price', color: PRIMARY_COLOR, type: 'line' },
                { label: 'Median price', color: PRICE_MED, type: 'line' },
                { label: 'Lowest price paid', color: PRICE_LOW, type: 'line' },
              ]}
            />
            <div className={css(styles.chartWrap)}>
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={chartData} margin={{ top: 16, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid stroke={BORDER_2} vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={{ stroke: BORDER_2 }}
                    tick={{ fontSize: 14, fontWeight: 600, fill: FG_3 }}
                  />
                  <YAxis
                    tick={{ fontSize: 14, fontWeight: 600, fill: FG_3 }}
                    tickFormatter={fmtPrice}
                    domain={priceDomain}
                    ticks={priceTicks}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ stroke: CURSOR_GREY, strokeWidth: 1, strokeDasharray: '3 3' }}
                    content={
                      <ChartTooltip
                        order={['max', 'avg', 'median', 'min']}
                        valueFormatter={(dataKey, val) => fmtPrice(val)}
                      />
                    }
                  />
                  {/* Shaded high–low band behind the lines. */}
                  <Area
                    dataKey="band"
                    stroke="none"
                    fill={PRIMARY_COLOR}
                    fillOpacity={0.08}
                    legendType="none"
                    tooltipType="none"
                    activeDot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey="max"
                    name="Highest"
                    stroke={PRICE_HIGH}
                    strokeWidth={3}
                    strokeDasharray="6 6"
                    dot={{
                      r: 4,
                      fill: WHITE,
                      stroke: PRICE_HIGH,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                    activeDot={{
                      r: 6,
                      fill: WHITE,
                      stroke: PRICE_HIGH,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                  />
                  <Line
                    dataKey="min"
                    name="Lowest"
                    stroke={PRICE_LOW}
                    strokeWidth={3}
                    strokeDasharray="6 6"
                    dot={{
                      r: 4,
                      fill: WHITE,
                      stroke: PRICE_LOW,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                    activeDot={{
                      r: 6,
                      fill: WHITE,
                      stroke: PRICE_LOW,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                  />
                  <Line
                    dataKey="median"
                    name="Median"
                    stroke={PRICE_MED}
                    strokeWidth={3}
                    strokeDasharray="3 6"
                    dot={{
                      r: 4,
                      fill: WHITE,
                      stroke: PRICE_MED,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                    activeDot={{
                      r: 6,
                      fill: WHITE,
                      stroke: PRICE_MED,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                  />
                  <Line
                    dataKey="avg"
                    name="Avg"
                    stroke={PRIMARY_COLOR}
                    strokeWidth={3}
                    dot={{
                      r: 4,
                      fill: WHITE,
                      stroke: PRIMARY_COLOR,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                    activeDot={{
                      r: 6,
                      fill: WHITE,
                      stroke: PRIMARY_COLOR,
                      strokeWidth: 3,
                      strokeDasharray: '0',
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* ── Distributor Details ──────────────────────────────────────── */}
      <div className={css(styles.section)}>
        <h3 className={css(styles.sectionTitle)}>Distributor Details</h3>
        <div className={css(styles.tableWrap)}>
          <Table className={css(styles.table)}>
            <thead>
              <tr>
                <th className={css(styles.th)}>Distributor</th>
                <th className={css(styles.th)}>SKU</th>
                <th className={css(styles.th)}>Pack / size</th>
                <th className={css(styles.thNum)}>Customers</th>
                <th className={css(styles.thNum)}>Annualized units</th>
                <th className={css(styles.thNum)}>Annualized GMV</th>
                <th className={css(styles.thNum)}>Catalog price</th>
                <th className={css(styles.thNum)}>Avg selling price</th>
              </tr>
            </thead>
            <tbody>
              {perf.length === 0 ? (
                <tr>
                  <td className={css(styles.td)} colSpan={8}>
                    No distributor activity in the last 30 days.
                  </td>
                </tr>
              ) : (
                perf.map((r, i) => (
                  <tr key={r.DISTRIBUTOR || i}>
                    <td className={css(styles.td, styles.strong)}>{r.DISTRIBUTOR || DASH}</td>
                    <td className={css(styles.td)}>{DASH}</td>
                    <td className={css(styles.td)}>{DASH}</td>
                    <td className={css(styles.tdNum)}>{fmtInt(r.CUSTOMERS)}</td>
                    <td className={css(styles.tdNum)}>{fmtInt(r.ANNUALIZED_UNITS)}</td>
                    <td className={css(styles.tdNum)}>{fmtMoney(r.ANNUALIZED_GMV)}</td>
                    <td className={css(styles.tdNum)}>{DASH}</td>
                    <td className={css(styles.tdNum)}>{fmtPrice(r.AVG_SELLING_PRICE)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* ── 3rd-party pricing (placeholders — no data source) ────────── */}
      <PlaceholderSection title="Broadliners" />
      <PlaceholderSection title="3rd Party Pricing (GPOs)" />

      {/* ── Suggested cashback ───────────────────────────────────────── */}
      <div className={css(styles.cashRow)}>
        <div className={css(styles.card)}>
          <span className={css(styles.cardLabel)}>Suggested Retention Cashback</span>
          <span className={css(styles.cardValue)}>{fmtPrice(cashback.retain)}</span>
        </div>
        <div className={css(styles.card)}>
          <span className={css(styles.cardLabel)}>Suggested Acquisition Cashback</span>
          <span className={css(styles.cardValue)}>{fmtPrice(cashback.acquire)}</span>
        </div>
      </div>
    </div>
  );
}

BetaSalesPricingTab.propTypes = { productId: PropTypes.string };

const numCell = {
  fontSize: 13,
  padding: '10px',
  borderBottom: `1px solid ${BORDER_2}`,
  textAlign: 'right',
  color: FG_1,
  fontVariantNumeric: 'tabular-nums',
};

const styles = StyleSheet.create({
  wrap: { display: 'flex', flexDirection: 'column', gap: 18, fontFamily: FONT_BODY },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 },
  card: {
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  cardLabel: {
    fontSize: 12,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  cardValue: { fontFamily: FONT_TITLE, fontSize: 24, fontWeight: FONT_WEIGHT_BOLD, color: FG_1 },
  delta: { fontSize: 12, fontWeight: FONT_WEIGHT_SEMIBOLD },
  deltaUp: { color: SUCCESS_TEXT },
  deltaDown: { color: ERROR_TEXT },
  deltaFlat: { color: FG_3 },
  section: {
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: 18,
  },
  chartHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  chartTabs: { display: 'inline-flex', backgroundColor: BG_2, borderRadius: 8, padding: 3, gap: 2 },
  chartTab: {
    border: 'none',
    backgroundColor: 'transparent',
    padding: '6px 14px',
    borderRadius: 6,
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_2,
    cursor: 'pointer',
  },
  chartTabActive: { backgroundColor: BG_1, color: FG_1, boxShadow: SHADOW_XS },
  dpFilter: { display: 'inline-flex', alignItems: 'center', gap: 8 },
  dpLabel: {
    fontSize: 11,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  select: {
    fontFamily: FONT_BODY,
    fontSize: 13,
    color: FG_1,
    padding: '6px 10px',
    border: `1px solid ${BORDER_2}`,
    borderRadius: 8,
    backgroundColor: BG_1,
    cursor: 'pointer',
  },
  note: { fontSize: 12, color: FG_3, fontStyle: 'italic', marginBottom: 8 },
  empty: { padding: 40, textAlign: 'center', color: FG_3, fontSize: 14 },
  // Top-left legend + shaded plot panel (mock `.chart-legend` / `.chart-wrap`).
  legend: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 18,
    margin: '14px 0 6px',
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_2,
  },
  legendSwatch: { display: 'inline-block', flexShrink: 0 },
  swatchBar: { width: 12, height: 12, borderRadius: 3 },
  swatchLine: { width: 18, height: 3, borderRadius: 2 },
  chartWrap: { marginTop: 6, padding: '8px 0', borderRadius: 8, backgroundColor: BG_2 },
  tooltip: {
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: 8,
    boxShadow: SHADOW_MD,
    padding: '8px 10px',
    minWidth: 150,
    fontFamily: FONT_BODY,
  },
  ttMonth: { fontSize: 12, fontWeight: FONT_WEIGHT_BOLD, color: FG_1, marginBottom: 4 },
  ttRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '2px 0',
  },
  ttLbl: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_2,
  },
  ttDot: { width: 8, height: 8, borderRadius: 9999, flexShrink: 0, display: 'inline-block' },
  ttVal: {
    fontSize: 12,
    fontWeight: FONT_WEIGHT_BOLD,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
  },
  sectionTitle: {
    fontFamily: FONT_BODY,
    fontSize: 16,
    fontWeight: FONT_WEIGHT_BOLD,
    color: FG_1,
    margin: '0 0 12px',
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    fontSize: 11,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '8px 10px',
    borderBottom: `1px solid ${BORDER_2}`,
    whiteSpace: 'nowrap',
  },
  thNum: {
    textAlign: 'right',
    fontSize: 11,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '8px 10px',
    borderBottom: `1px solid ${BORDER_2}`,
    whiteSpace: 'nowrap',
  },
  td: { fontSize: 13, color: FG_2, padding: '10px', borderBottom: `1px solid ${BORDER_2}` },
  tdNum: numCell,
  strong: { fontWeight: FONT_WEIGHT_SEMIBOLD, color: FG_1 },
  placeholder: {
    border: `1px dashed ${BORDER_2}`,
    borderRadius: 8,
    padding: 24,
    textAlign: 'center',
    color: FG_3,
    fontSize: 13,
  },
  cashRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
});

export default BetaSalesPricingTab;
